import { useRefFn, getEmptyObject, EMPTY_TUPLE } from './helpers';
import { useEffect, useMemo, useRef } from 'react';
/**
 * Accepts an Observable and optional `next`, `error`, `complete` functions.
 * These functions must be in correct order.
 * Use `undefined` or `null` for placeholder.
 *
 * Subscription will unsubscribe when unmount, you can also
 * unsubscribe manually.
 *
 * Note that changes of callbacks will not trigger
 * an emission. If you need that just create another
 * Observable of the callback with [[useObservable]].
 *
 * (From v2.0) You can access closure directly inside callback like in `useEffect`.
 * `useSubscription` will ensure the latest callback is called.
 *
 * (From v2.3.4) when the Observable changes `useSubscription` will automatically
 * unsubscribe the old one and resubscribe to the new one.
 *
 * ⚠ **Note:** Due to the design of RxJS, once an error occurs in an observable, the observable
 * is killed.
 * You should prevent errors from reaching observables or `catchError` in sub-observables.
 * You can also make the observable as state and replace it on error.
 * `useSubscription` will automatically switch to the new one.
 *
 * @template TInput Input value within Observable.
 *
 * @param input$ Input Observable.
 * @param next Notify when a new value is emitted.
 * @param error Notify when a new error is thrown.
 * @param complete Notify when the Observable is complete.
 */
export function useSubscription(input$, next, error, complete) {
    const cbRef = useRefFn(getEmptyObject);
    cbRef.current.next = next;
    cbRef.current.error = error;
    cbRef.current.complete = complete;
    const subscriptionRef = useRef();
    subscriptionRef.current = useMemo(() => {
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
        }
        return input$.subscribe({
            next: value => cbRef.current.next && cbRef.current.next(value),
            error: error => {
                if (cbRef.current.error) {
                    cbRef.current.error(error);
                }
                else {
                    throw error;
                }
            },
            complete: () => cbRef.current.complete && cbRef.current.complete()
        });
    }, [input$]);
    // unsubscribe when unmount
    useEffect(() => () => subscriptionRef.current.unsubscribe(), EMPTY_TUPLE);
    return subscriptionRef.current;
}
//# sourceMappingURL=use-subscription.js.map